import React, { FC } from "react";
import Button from "components/Button/Button";
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import { openExternalURL } from "../../../../utils/common";
import formatter from "../../../../utils/formatter";
import {
  InfoRow,
  InfoRowLabel,
  SectionButtonWrapper,
  SectionTitle,
} from "./InboundShipmentPanel.styles";

interface InboundLabelSectionProps {
  label: any;
  packages: any;
  isCancelingLabel: boolean;
  onCancelLabel: () => void;
}

const InboundLabelSection: FC<InboundLabelSectionProps> = ({
  label,
  packages,
  isCancelingLabel,
  onCancelLabel,
}) => {
  const { t } = useTranslation("common");
  const currencySymbol = useCurrencySymbol();
  const trackingNumber = label.get("tracking_number");

  // Per-package labels only add information on multi-package shipments — a
  // single package duplicates the master label.
  const labeledPackages = (packages || List()).filter((pkg: any) =>
    pkg.get("label"),
  );
  const showPackageLabels = (packages?.size || 0) > 1 && labeledPackages.size > 0;

  const handleDownload = () => {
    openExternalURL(label.get("label_pdf_url"), "noopener,noreferrer");
  };

  return (
    <div>
      <SectionTitle>{t("inbound.label.title")}</SectionTitle>
      {trackingNumber && (
        <InfoRow>
          <InfoRowLabel>{t("inbound.label.trackingNumber")}</InfoRowLabel>
          <CopyToClipboard text={trackingNumber}>
            {trackingNumber}
          </CopyToClipboard>
        </InfoRow>
      )}
      <InfoRow>
        <InfoRowLabel>{t("inbound.label.cost")}</InfoRowLabel>
        <span>
          {currencySymbol}
          {formatter.currency(label.get("shipment_cost"))}
        </span>
      </InfoRow>
      {/* Only the transient ship response carries insurance_cost — the row
          disappears after the detail refetch replaces it. */}
      {Number(label.get("insurance_cost")) > 0 && (
        <InfoRow>
          <InfoRowLabel>{t("inbound.label.insurance")}</InfoRowLabel>
          <span>
            {currencySymbol}
            {formatter.currency(label.get("insurance_cost"))}
          </span>
        </InfoRow>
      )}
      {label.get("total_charged") && (
        <InfoRow>
          <InfoRowLabel>{t("inbound.label.totalCharged")}</InfoRowLabel>
          <span>
            {currencySymbol}
            {formatter.currency(label.get("total_charged"))}
          </span>
        </InfoRow>
      )}
      {showPackageLabels && (
        <>
          <SectionTitle>{t("inbound.label.packagesTitle")}</SectionTitle>
          {labeledPackages.map((pkg: any) => (
            <InfoRow key={pkg.get("id")}>
              <InfoRowLabel>
                {t("inbound.form.packageTitle", {
                  number: pkg.get("sequence"),
                })}
              </InfoRowLabel>
              <CopyToClipboard text={pkg.getIn(["label", "tracking_number"])}>
                {pkg.getIn(["label", "tracking_number"])}
              </CopyToClipboard>
              <Button
                type={ButtonType.Button}
                onClick={() =>
                  openExternalURL(
                    pkg.getIn(["label", "label_pdf_url"]),
                    "noopener,noreferrer",
                  )
                }
              >
                {t("inbound.label.download")}
              </Button>
            </InfoRow>
          ))}
        </>
      )}
      <SectionButtonWrapper>
        <Button
          type={ButtonType.Button}
          variant={ButtonVariant.Bordered}
          color={ButtonColor.Red}
          isLoading={isCancelingLabel}
          disabled={isCancelingLabel}
          onClick={onCancelLabel}
        >
          {t("inbound.label.cancel")}
        </Button>
        <Button
          type={ButtonType.Button}
          variant={ButtonVariant.Filled}
          color={ButtonColor.Primary}
          onClick={handleDownload}
        >
          {t("inbound.label.download")}
        </Button>
      </SectionButtonWrapper>
    </div>
  );
};

export default React.memo(InboundLabelSection);
